import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, rupiah } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { DepartureBoard } from "@/components/DepartureBoard";
import { BookingDialog } from "@/components/BookingDialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ChevronRight, MapPin, ArrowRight, Star, Quote } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import NotFound from "@/pages/NotFound";

export default function CityPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    let direction = "from";
    let citySlug = slug;
    if (slug.startsWith("travel-dari-")) { direction = "from"; citySlug = slug.slice("travel-dari-".length); }
    else if (slug.startsWith("travel-ke-")) { direction = "to"; citySlug = slug.slice("travel-ke-".length); }
    api.get(`/cities/${citySlug}?direction=${direction}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <NotFound message="Halaman kota tidak ditemukan." />;

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-7xl px-4 py-20 text-muted-foreground">Memuat...</div>
      </div>
    );
  }

  const prep = data.direction === "from" ? "dari" : "ke";

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={data.title}
        description={`Temukan ${data.route_count} rute travel door to door ${prep} ${data.name} dengan ${data.schedule_count} jadwal aktif di Digital Terminal Travele. Bandingkan harga, armada, dan sisa kursi.`}
        schema={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: (data.faq || []).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }}
      />
      <Navbar />
      <Breadcrumbs
        items={[
          { name: "Beranda", path: "/" },
          { name: "Kota Layanan" },
          { name: `Travel ${prep} ${data.name}` },
        ]}
      />

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <p className="flex items-center gap-1 font-mono-terminal text-xs uppercase tracking-widest text-primary">
          <MapPin className="h-3.5 w-3.5" /> {data.province}
        </p>
        <h1 className="mt-2 max-w-3xl font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl lg:text-5xl">
          Travel {prep} {data.name}
        </h1>
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
          Temukan {data.route_count} rute travel door to door {prep} {data.name} dengan {data.schedule_count} jadwal aktif di Digital Terminal Travele.
          Bandingkan harga, armada, dan sisa kursi, lalu pesan lewat WhatsApp Admin resmi.
        </p>

        <section className="mt-8 grid gap-4 md:grid-cols-2" data-testid="city-info-section">
          <div className="border border-border bg-card p-5">
            <h2 className="font-display text-lg font-bold">Travel Door to Door {data.name}</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Layanan travel door to door {prep} {data.name} menjemput penumpang langsung dari alamat dan mengantar sampai tujuan tanpa perlu ke pool.
              Cocok untuk perjalanan antarkota bersama keluarga—armada ber-AC, nyaman, dan berangkat sesuai jadwal. Bandingkan harga tiket dan pilihan armada dari beberapa driver terverifikasi, lalu pesan lewat Admin Travele.
            </p>
          </div>
          <div className="border border-border bg-card p-5">
            <h2 className="font-display text-lg font-bold">Charter Drop Off {data.name}</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Butuh privasi atau bepergian rombongan? Charter drop off {prep} {data.name} menyewakan satu kendaraan penuh dengan rute dan titik jemput fleksibel.
              Ideal untuk keluarga besar, acara, atau perjalanan dinas. Tarif dikonfirmasi Admin sesuai rute, jarak, dan kebutuhan Anda.
            </p>
          </div>
        </section>

        {/* Destinasi rute */}
        <h2 className="mb-4 mt-10 font-display text-2xl font-bold tracking-tight">
          {data.direction === "from" ? `Tujuan Populer dari ${data.name}` : `Berangkat dari Kota Menuju ${data.name}`}
        </h2>
        {data.routes.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {data.routes.map((r) => (
              <Link
                key={r.slug}
                to={`/${r.slug}`}
                data-testid={`city-route-${r.slug}`}
                className="group border border-border bg-card p-5 transition-colors hover:border-primary"
              >
                <div className="flex items-center justify-between font-mono-terminal text-xs uppercase text-muted-foreground">
                  <span>{r.count} jadwal</span>
                  <ArrowRight className="h-4 w-4 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
                <div className="mt-2 font-display text-lg font-bold">{r.origin} → {r.destination}</div>
                <div className="mt-1 text-sm text-muted-foreground">Mulai {rupiah(r.min_price)}</div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="border border-border bg-card p-6 text-muted-foreground">Belum ada rute aktif {prep} {data.name}.</p>
        )}

        {/* Jadwal */}
        {data.schedules.length > 0 && (
          <>
            <h2 className="mb-4 mt-12 font-display text-2xl font-bold tracking-tight">Jadwal Keberangkatan</h2>
            <DepartureBoard schedules={data.schedules} onBook={setBooking} showRoute={true} />
          </>
        )}

        {/* FAQ */}
        <h2 className="mb-4 mt-12 font-display text-2xl font-bold tracking-tight">Pertanyaan Umum</h2>
        <Accordion type="single" collapsible className="border border-border bg-card px-4">
          {data.faq.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`city-faq-${i}`}>
              <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Ulasan */}
        {data.reviews?.length > 0 && (
          <>
            <h2 className="mb-4 mt-12 font-display text-2xl font-bold tracking-tight">Ulasan Penumpang</h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {data.reviews.map((rv, i) => (
                <div key={i} className="border border-border bg-card p-5" data-testid={`city-review-${i}`}>
                  <Quote className="h-5 w-5 text-primary/50" />
                  <div className="mt-2 flex items-center gap-1 text-primary">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className={`h-3.5 w-3.5 ${s < Math.round(rv.average) ? "fill-primary" : "text-muted-foreground"}`} />
                    ))}
                    <span className="ml-1 font-mono-terminal text-xs text-muted-foreground">{rv.average?.toFixed(1)}</span>
                  </div>
                  {rv.comment && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">"{rv.comment}"</p>}
                  <div className="mt-3 text-sm font-semibold">{rv.passenger_name}</div>
                  <div className="font-mono-terminal text-[10px] uppercase text-muted-foreground">{rv.driver_name}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Kota terkait */}
        {data.related.length > 0 && (
          <>
            <h2 className="mb-4 mt-12 font-display text-2xl font-bold tracking-tight">Kota Layanan Lainnya</h2>
            <div className="flex flex-wrap gap-2">
              {data.related.map((c) => (
                <Link
                  key={c.slug}
                  to={`/travel-dari-${c.slug}`}
                  className="border border-border bg-card px-4 py-2 text-sm transition-colors hover:border-primary"
                >
                  Travel dari {c.name}
                </Link>
              ))}
            </div>
          </>
        )}
      </div>

      <Footer />
      <WhatsAppFloat />
      <BookingDialog schedule={booking} open={!!booking} onClose={() => setBooking(null)} />
    </div>
  );
}
